import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/categories";

const FORM_ADDR = "https://readdy.ai/api/form/db91709rh5tg6sock29g";

const values = [
  { icon: "ri-film-line", title: "汇聚精品", text: "聚合影视剧、综艺、原创与纪录片等高清内容，一站直达。" },
  { icon: "ri-shield-check-line", title: "免费无套路", text: "无需注册、无需付费，打开即可浏览与观看。" },
  { icon: "ri-search-eye-line", title: "轻松发现", text: "多条件筛选、关键词搜索与专题推荐，帮你快速找到想看的。" },
  { icon: "ri-device-line", title: "多端适配", text: "手机与电脑均可流畅浏览，随时随地享受高清体验。" },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  usePageMeta(
    "关于我们 - 精品视频",
    "了解精品视频的平台定位、内容范围与联系方式。我们致力于为喜爱免费视频的用户提供高清、便捷的在线观看体验。",
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "contact_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      params.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (typeof parsed === "object" && parsed ? "" : responseText);
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch (err) {
      setStatus("error");
      setFormError(err instanceof Error ? err.message : "网络异常，请稍后重试。");
    }
  };

  return (
    <div className="w-full">
      <section className="bg-primary-950 text-background-50">
        <div className="w-full px-4 md:px-8 pt-28 md:pt-36 pb-12 md:pb-16">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-secondary-500 text-background-50 text-xs font-medium mb-4">
            <i className="ri-emotion-happy-line" />
            关于精品视频
          </span>
          <h1 className="text-3xl md:text-5xl font-black max-w-3xl leading-tight">
            让每一个人都能轻松看好视频
          </h1>
          <p className="mt-5 text-sm md:text-base text-background-200 max-w-2xl leading-relaxed">
            精品视频是聚焦高清免费视频资源的聚合平台。我们汇聚影视剧、娱乐综艺、原创内容与纪录片，
            用清晰的分类、便捷的筛选和用心的推荐，帮助每一位喜爱视频的用户快速找到并观看好内容。
          </p>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <SectionHeading icon="ri-focus-3-line" title="我们的坚持" subtitle="四个关键词，定义精品视频的体验" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {values.map((v) => (
            <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex items-center justify-center w-11 h-11 rounded-lg bg-primary-100 text-primary-600">
                <i className={`${v.icon} text-2xl`} />
              </span>
              <h3 className="mt-3 text-base font-bold text-foreground-950">{v.title}</h3>
              <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16">
          <SectionHeading icon="ri-apps-2-line" title="内容范围" subtitle="覆盖多种类型，持续扩充内容库" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 hover:bg-primary-50/60 cursor-pointer transition-colors"
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent-100 text-accent-700">
                  <i className={`${c.icon} text-xl`} />
                </span>
                <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                  {c.name}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <SectionHeading icon="ri-customer-service-2-line" title="联系我们" subtitle="你的建议对我们很重要" />
            <p className="text-sm text-foreground-600 leading-relaxed">
              如果你对内容、功能或体验有任何想法与建议，欢迎通过右侧的站内反馈表单告诉我们，
              我们会在收到后尽快查看处理。
            </p>
            <div className="mt-5 space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-background-100 text-primary-600">
                  <i className="ri-mail-line" />
                </span>
                <a href="mailto:feedback@jingpin-video.com" className="text-foreground-800 hover:text-primary-600 transition-colors">
                  feedback@jingpin-video.com
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-background-100 text-primary-600">
                  <i className="ri-time-line" />
                </span>
                <span className="text-foreground-700">工作日 09:00 - 18:00 在线</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="text-base font-bold text-foreground-950 mb-1">站内反馈</h3>
              <p className="text-xs text-foreground-500 mb-5">填写以下信息，我们会认真阅读每一条反馈。</p>

              {status === "success" ? (
                <div className="rounded-lg bg-accent-50 border border-accent-200 p-6 text-center">
                  <i className="ri-checkbox-circle-line text-3xl text-accent-600" />
                  <p className="mt-2 text-sm font-medium text-accent-800">反馈已提交，感谢你的支持！</p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-3 text-xs text-accent-700 underline cursor-pointer"
                  >
                    再次反馈
                  </button>
                </div>
              ) : (
                <form id="about-feedback-form" onSubmit={handleSubmit} data-readdy-form="db91709rh5tg6sock29g">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-foreground-700 mb-1.5" htmlFor="about-name">称呼</label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        placeholder="请输入你的称呼"
                        className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-foreground-700 mb-1.5" htmlFor="about-email">邮箱</label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        placeholder="便于我们回复你"
                        className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm text-foreground-700 mb-1.5" htmlFor="about-topic">反馈类型</label>
                    <select
                      id="about-topic"
                      name="topic"
                      defaultValue="内容建议"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
                    >
                      <option value="内容建议">内容建议</option>
                      <option value="功能反馈">功能反馈</option>
                      <option value="播放问题">播放问题</option>
                      <option value="合作咨询">合作咨询</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm text-foreground-700 mb-1.5" htmlFor="about-message">反馈内容</label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请描述你的建议或遇到的问题（最多 500 字）"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
                    />
                  </div>

                  <input
                    type="text"
                    name="contact_alt"
                    className="comment-note-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === "error" && (
                    <p className="mt-3 text-sm text-secondary-700">{formError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="mt-5 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors"
                  >
                    <i className="ri-send-plane-line" />
                    {status === "submitting" ? "提交中..." : "提交反馈"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        <div className="mt-12 md:mt-16 rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
          <h3 className="text-base font-bold text-foreground-950 mb-2">版权声明</h3>
          <p className="text-sm text-foreground-600 leading-relaxed">
            本站为高清免费视频资源聚合平台，页面内容仅用于学习交流与演示展示，不提供视频下载、付费与注册服务。
            站内视频封面与相关素材均为示意内容，如涉及版权问题，请通过上方邮箱与我们联系，我们会及时处理。
          </p>
          <p className="mt-4 text-xs text-foreground-500">© 2026 精品视频 · 保留所有权利</p>
        </div>
      </div>
    </div>
  );
}